import { format } from 'date-fns'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { useTypedSelector } from '../..'
import { isCommuterUser } from '../../lib/roleUtils'
import { generateEquipmnentInfoKeyFromTask } from '../../lib/trainEquipmentUtils'
import { selectTrainEquipmentCirculationByKey } from '../../reducers/trainEquipmentCirculationSlice'
import { Task } from '../../types'
import { EquipmentCirculation, TrainEquipmentCirculation, TrainType } from '../../types/Input'
import { Text } from './TaskDetails'
import { TrainEquipmentPunctuality } from './TrainEquipmentPunctuality'
import { useTaskEquipmentPunctualityFetch } from './useTaskEquipmentPunctualityFetch'

type TrainEquipmentProps = {
  task: Task
  type: TrainType
}

type RestaurantEquipmentType = 'nighttrain' | 'restaurant' | 'cart' | 'sm3' | 'sm6'

type EquipmentItem = {
  type: RestaurantEquipmentType
  count: number
  wagonCode: string
}

type TrainInfoTextProps = {
  train: EquipmentCirculation
  type: TrainType
  t: TFunction
  hideTimestamp?: boolean
}

const formatTimeDisplay = (train: EquipmentCirculation, type: TrainType) => {
  return format(new Date(type === 'to' ? train.timestampStart : train.timestampEnd), 'HH:mm')
}

const getTrainId = (train: EquipmentCirculation) => `${train.trainNumber}-${train.timestampStart}`

const mapTrainForPunctualityFetch = ({
  trainNumber,
  operatingDay,
  timestampEnd,
}: EquipmentCirculation) => ({
  trainNumber,
  operatingDate: operatingDay,
  timestamp: timestampEnd,
})

const getOverlappingTrainIds = (
  trainEquipmentCirculation: TrainEquipmentCirculation | undefined
): Set<string> => {
  if (!trainEquipmentCirculation) return new Set()

  const fromTrains = trainEquipmentCirculation.fromTrain || []
  const toTrains = trainEquipmentCirculation.toTrain || []

  const fromIds = new Set(fromTrains.map(getTrainId))
  const overlapping = new Set<string>()

  for (const train of toTrains) {
    const id = getTrainId(train)
    if (fromIds.has(id)) {
      overlapping.add(id)
    }
  }

  return overlapping
}

const isOverlapping = (train: EquipmentCirculation, overlappingIds: Set<string>): boolean =>
  overlappingIds.has(getTrainId(train))

const hasRestaurantEquipment = (train: EquipmentCirculation): boolean =>
  (train?.equipment?.filter((item) => item?.type) || []).length > 0

const getRestaurantEquipment = (train: EquipmentCirculation): EquipmentItem[] =>
  (train?.equipment?.filter((item) => item?.type) || []) as EquipmentItem[]

const TrainInfoText = ({ train, type, t, hideTimestamp = false }: TrainInfoTextProps) => (
  <Text type={type}>
    {`${t(`${type}Train`)} ${train.trainNumber} `}
    {!hideTimestamp && <strong>({formatTimeDisplay(train, type)})</strong>}{' '}
    {type === 'from' && (
      <TrainEquipmentPunctuality
        trainNumber={train.trainNumber}
        operatingDate={train.operatingDay}
      />
    )}
  </Text>
)

const TrainEquipmentDetails = ({
  train,
  type,
  t,
  hideTimestamp,
}: {
  train: EquipmentCirculation
  type: TrainType
  t: TFunction
  hideTimestamp: boolean
}) => {
  const restaurantEquipment = getRestaurantEquipment(train)
  if (restaurantEquipment.length === 0) return null

  return (
    <div key={getTrainId(train)}>
      {restaurantEquipment.map((eq) => (
        <div key={`${eq.type}-${eq.wagonCode}`}>
          <Text type={type}>
            {`${eq.count} ${t(`restaurantType.${eq.type.toLowerCase()}`)} ${eq.wagonCode}`}
          </Text>
        </div>
      ))}
      <TrainInfoText train={train} type={type} t={t} hideTimestamp={hideTimestamp} />
    </div>
  )
}

export const TrainEquipment = ({ task, type }: TrainEquipmentProps) => {
  const { t } = useTranslation()

  const user = useTypedSelector((state) => state.user)
  const hideContent = isCommuterUser(user)

  const equipmentInfoKey = generateEquipmnentInfoKeyFromTask(task)
  const trainEquipmentCirculation = useTypedSelector((state) =>
    selectTrainEquipmentCirculationByKey(state, equipmentInfoKey)
  )

  const trains = trainEquipmentCirculation?.[`${type}Train`] || []

  const overlappingTrainIds = getOverlappingTrainIds(trainEquipmentCirculation)

  useTaskEquipmentPunctualityFetch(
    type === 'from' && !hideContent,
    trains.map(mapTrainForPunctualityFetch),
    task.taskStartDateTime
  )

  if (hideContent || trains.length === 0) return null

  const trainsWithWagons: EquipmentCirculation[] = []
  const trainsWithoutWagons: EquipmentCirculation[] = []
  for (const train of trains) {
    if (hasRestaurantEquipment(train)) trainsWithWagons.push(train)
    else trainsWithoutWagons.push(train)
  }

  if (user.waiter || user.conductor)
    return (
      <>
        {trainsWithWagons.map((train) => (
          <TrainEquipmentDetails
            key={getTrainId(train)}
            train={train}
            type={type}
            t={t}
            hideTimestamp={isOverlapping(train, overlappingTrainIds)}
          />
        ))}
        {user.conductor &&
          trainsWithoutWagons.map((train) => (
            <div key={getTrainId(train)}>
              <TrainInfoText
                train={train}
                type={type}
                t={t}
                hideTimestamp={isOverlapping(train, overlappingTrainIds)}
              />
            </div>
          ))}
      </>
    )

  return (
    <>
      {trains.map((train) => (
        <div key={getTrainId(train)}>
          <TrainInfoText
            train={train}
            type={type}
            t={t}
            hideTimestamp={isOverlapping(train, overlappingTrainIds)}
          />
        </div>
      ))}
    </>
  )
}
